import type HighchartsReact from 'highcharts-react-official';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { CheckOutline, DocumentDownloadOutline, DuplicateOutline, Flex, IconButton, PhotographOutline, Tooltip } from '@bonusly/design-system';

import { useBizyEvidenceAnalytics } from './bizy-evidence-analytics-context';
import { exportFilename } from './export-filename';

// Matches the table action bar (bizy-table-shell) so chart, matrix, and table
// exports look identical.
const ACTION_STYLES = {
	size: 'xs' as const,
	variant: 'ghost' as const,
	colorScheme: 'secondary' as const,
	color: 'info.icon',
	borderRadius: '8px',
	_hover: { bg: 'secondary.bgAlpha', color: 'secondary.fgActive' },
	_active: { bg: 'secondary.bgAlphaHover', color: 'secondary.fgActive' },
};

interface HighchartsExportBarProps {
	chartRef: RefObject<HighchartsReact.RefObject>;
	/** Title the download filename derives from, and the heading drawn into the
	 *  exported image. */
	title?: string;
	/** Axis metric names — drawn as SVG axis titles in the exported image only
	 *  (on-screen they live in an HTML caption the narrow panel needs). */
	xLabel?: string;
	yLabel?: string;
	/** Extra line under the title in the export (e.g. a bubble's size metric). */
	subtitle?: string;
	/** Filename fallback when there's no title-specific one. */
	fallback: string;
	/** Analytics `variant` value (e.g. "bar", "line", "heatmap"). */
	variant: string;
	/** Test-id / class prefix so each surface stays individually addressable. */
	idPrefix: string;
}

/**
 * Shared Copy / CSV / PNG export action bar for Highcharts-backed nodes (chart,
 * matrix). Hover-revealed, matching the table chrome. Copy + CSV serialize the
 * series data (getCSV / downloadCSV); PNG rasterizes client-side
 * (exportChartLocal) with the HTML axis/legend labels re-rendered as native SVG
 * text so offline rasterization captures them. The parent must reveal
 * `.{idPrefix}-actions` on hover.
 */
export function HighchartsExportBar({ chartRef, title, xLabel, yLabel, subtitle, fallback, variant, idPrefix }: HighchartsExportBarProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const { conversationId, messageId } = useBizyEvidenceAnalytics();
	const filename = exportFilename(title, fallback);
	const [copied, setCopied] = useState(false);
	const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		return () => {
			if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
		};
	}, []);

	const handleCopy = async () => {
		const chart = chartRef.current?.chart;
		if (!chart) return;
		try {
			await navigator.clipboard.writeText(chart.getCSV());
			setCopied(true);
			if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
			copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
			track(AnalyticsEvent.Bizy.ChartExported, { format: 'copy', variant, conversationId, messageId, path: window.location.pathname });
		} catch {
			// Clipboard write failed silently
		}
	};

	const handleDownloadCsv = () => {
		const chart = chartRef.current?.chart;
		if (!chart) return;
		chart.getFilename = () => filename;
		chart.downloadCSV();
		track(AnalyticsEvent.Bizy.ChartExported, { format: 'csv', variant, conversationId, messageId, path: window.location.pathname });
	};

	const handleDownloadPng = () => {
		const chart = chartRef.current?.chart;
		if (!chart) return;
		chart.exportChartLocal(
			{ type: 'image/png', filename },
			{
				// On-screen the title/axes live in HTML around the SVG; the export
				// re-adds them so the downloaded image is legible standalone. Inject
				// only the fields we were given so we never clobber a node's own
				// native SVG title/axis titles (e.g. a heatmap) with empty strings.
				...(title ? { title: { text: title } } : {}),
				...(subtitle ? { subtitle: { text: subtitle } } : {}),
				xAxis: { labels: { useHTML: false }, ...(xLabel ? { title: { text: xLabel } } : {}) },
				yAxis: { labels: { useHTML: false }, ...(yLabel ? { title: { text: yLabel } } : {}) },
				legend: { useHTML: false },
			}
		);
		track(AnalyticsEvent.Bizy.ChartExported, { format: 'png', variant, conversationId, messageId, path: window.location.pathname });
	};

	return (
		<Flex className={`${idPrefix}-actions`} gap="4px" opacity={0} transition="opacity 0.15s ease-in-out">
			<Tooltip label={copied ? t('feedback.copied') : t('feedback.copy')} placement="top">
				<IconButton
					data-testid={`${idPrefix}-copy`}
					aria-label={t('feedback.copy')}
					icon={copied ? <CheckOutline boxSize={5} /> : <DuplicateOutline boxSize={5} />}
					{...ACTION_STYLES}
					onClick={handleCopy}
				/>
			</Tooltip>
			<Tooltip label={t('feedback.downloadCsv')} placement="top">
				<IconButton
					data-testid={`${idPrefix}-download-csv`}
					aria-label={t('feedback.downloadCsv')}
					icon={<DocumentDownloadOutline boxSize={5} />}
					{...ACTION_STYLES}
					onClick={handleDownloadCsv}
				/>
			</Tooltip>
			<Tooltip label={t('feedback.downloadImage')} placement="top">
				<IconButton
					data-testid={`${idPrefix}-download-png`}
					aria-label={t('feedback.downloadImage')}
					icon={<PhotographOutline boxSize={5} />}
					{...ACTION_STYLES}
					onClick={handleDownloadPng}
				/>
			</Tooltip>
		</Flex>
	);
}
